import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Injectable,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgbDateParserFormatter, NgbDateStruct } from "@ng-bootstrap/ng-bootstrap";
import { SliderService } from "../dp-home/home-service/slider.service";
import { VisitService } from "../dp-services/visit.service";
import { VisitPrice } from "./VisitPrice.model";

@Injectable()
export class ItalianDateParserFormatter extends NgbDateParserFormatter {
  parse(value: string): NgbDateStruct | null {
    if (!value) {
      return null;
    }
    const parts = value.trim().split("-");
    if (parts.length === 3) {
      return {
        day: parseInt(parts[0], 10),
        month: parseInt(parts[1], 10),
        year: parseInt(parts[2], 10),
      };
    }
    return null;
  }

  format(date: NgbDateStruct | null): string {
    if (!date) {
      return "";
    }
    const dd = String(date.day).padStart(2, "0");
    const mm = String(date.month).padStart(2, "0");
    return `${dd}-${mm}-${date.year}`;
  }
}

@Component({
  selector: "app-visit",
  templateUrl: "./visit.component.html",
  styleUrls: ["./visit.component.scss"],
  providers: [
    { provide: NgbDateParserFormatter, useClass: ItalianDateParserFormatter },
  ],
})
export class VisitComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("privacyCheckbox") privacyCheckbox: ElementRef<HTMLInputElement>;
  @ViewChild("emailDestinatarioRegaloInput")
  emailDestinatarioRegaloInput: ElementRef<HTMLInputElement>;
  @ViewChild("ticketLayout") ticketLayoutRef: ElementRef<HTMLElement>;
  @ViewChild("ticketStub") ticketStubRef: ElementRef<HTMLElement>;

  private readonly stickyTopOffset = 20;
  private readonly stickyBreakpoint = 860;
  private cachedColumnWidth = 0;
  private cachedColumnLeftViewport = 0;
  private cachedColumnOffsetInLayout = 0;
  private stickyTickScheduled = false;
  private onScrollOrResize = () => this.scheduleStickyTicketUpdate();

  totaleVisita = 0;
  visitForm!: FormGroup;
  submitted = false;

  timeOptions: { id: string; ora: string }[] = [];
  tomorrow: any;

  caseificiAperti: boolean = false;

  minDate: NgbDateStruct;
  dateModel: NgbDateStruct;
  availableDates: Set<string> = new Set();
  isDateDisabled = (date: NgbDateStruct): boolean => {
    return !this.availableDates.has(this.ngbDateToString(date));
  };

  isDegustazioneAvailable = false;
  isDegustazioneAnoliniAvailable = false;
  isDegustazionePaninoAvailable = false;

  public sliders = [];

  prezzi: VisitPrice | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private slidersService: SliderService,
    private httpVisit: VisitService,
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2,
    private ngZone: NgZone,
  ) {}

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener("scroll", this.onScrollOrResize, {
        passive: true,
      });
      window.addEventListener("resize", this.onScrollOrResize);
      this.updateStickyTicket();
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener("scroll", this.onScrollOrResize);
    window.removeEventListener("resize", this.onScrollOrResize);
  }

  private scheduleStickyTicketUpdate(): void {
    if (this.stickyTickScheduled) {
      return;
    }
    this.stickyTickScheduled = true;
    requestAnimationFrame(() => {
      this.stickyTickScheduled = false;
      this.updateStickyTicket();
    });
  }

  private resetStickyTicketStyle(stubEl: HTMLElement): void {
    this.renderer.removeStyle(stubEl, "position");
    this.renderer.removeStyle(stubEl, "top");
    this.renderer.removeStyle(stubEl, "left");
    this.renderer.removeStyle(stubEl, "width");
  }

  // Il biglietto resta fisso a destra durante lo scroll del form (solo su web):
  // implementato via JS perché position:sticky non funziona qui, dato che
  // html/body diventano involontariamente scroll-container per via di
  // "overflow-x: hidden" nel tema, il che rompe l'ancoraggio al viewport.
  private updateStickyTicket(): void {
    const layoutEl = this.ticketLayoutRef?.nativeElement;
    const stubEl = this.ticketStubRef?.nativeElement;
    if (!layoutEl || !stubEl) {
      return;
    }

    if (window.innerWidth <= this.stickyBreakpoint) {
      this.resetStickyTicketStyle(stubEl);
      return;
    }

    const isCurrentlyPinned =
      stubEl.style.position === "fixed" || stubEl.style.position === "absolute";

    if (!isCurrentlyPinned) {
      const naturalRect = stubEl.getBoundingClientRect();
      const layoutRectNow = layoutEl.getBoundingClientRect();
      this.cachedColumnWidth = naturalRect.width;
      this.cachedColumnLeftViewport = naturalRect.left;
      this.cachedColumnOffsetInLayout = naturalRect.left - layoutRectNow.left;
    }

    const layoutRect = layoutEl.getBoundingClientRect();
    const stubHeight = stubEl.offsetHeight;

    if (layoutRect.top > this.stickyTopOffset) {
      this.resetStickyTicketStyle(stubEl);
      return;
    }

    const maxTop = layoutEl.offsetHeight - stubHeight;
    const scrolledPastTop = this.stickyTopOffset - layoutRect.top;

    if (scrolledPastTop >= maxTop) {
      this.renderer.setStyle(stubEl, "position", "absolute");
      this.renderer.setStyle(stubEl, "top", `${maxTop}px`);
      this.renderer.setStyle(stubEl, "left", `${this.cachedColumnOffsetInLayout}px`);
      this.renderer.setStyle(stubEl, "width", `${this.cachedColumnWidth}px`);
    } else {
      this.renderer.setStyle(stubEl, "position", "fixed");
      this.renderer.setStyle(stubEl, "top", `${this.stickyTopOffset}px`);
      this.renderer.setStyle(stubEl, "left", `${this.cachedColumnLeftViewport}px`);
      this.renderer.setStyle(stubEl, "width", `${this.cachedColumnWidth}px`);
    }
  }

  ngOnInit(): void {
    var date = new Date();
    this.caseificiAperti = date < new Date("2025-10-06");
    // add a day
    this.tomorrow = date.setDate(date.getDate() + 1);

    const tomorrowDate = new Date(this.tomorrow);
    this.minDate = {
      year: tomorrowDate.getFullYear(),
      month: tomorrowDate.getMonth() + 1,
      day: tomorrowDate.getDate(),
    };

    this.timeOptions = [
      {
        id: "",
        ora: "Seleziona data e lingua",
      },
    ];
    this.visitForm = this.formBuilder.group({
      FirstName: ["", [Validators.required, Validators.maxLength(50)]],
      LastName: ["", [Validators.required, Validators.maxLength(50)]],
      Phone: [
        "",
        [
          Validators.required,
          Validators.pattern("[0-9]+"),
          Validators.maxLength(15),
        ],
      ],
      Email: ["", [Validators.required, Validators.email]],
      Nperson: [
        1,
        [
          Validators.required,
          Validators.pattern("[0-9]+"),
          Validators.maxLength(2),
        ],
      ],
      Nchild: ["", [Validators.pattern("[0-9]+"), Validators.maxLength(2)]],
      NchildUnder6: [
        0,
        [Validators.pattern("[0-9]+"), Validators.maxLength(2)],
      ],
      Language: ["italiano", [Validators.required]],
      VisitDate: [this.timeOptions[0].id, [Validators.required]],
      VisitTime: ["", [Validators.required]],
      Tasting: [false, [Validators.required]],
      Tasting2: [false, [Validators.required]],
      Tasting3: [false, [Validators.required]],
      Paese: ["", [Validators.nullValidator]],
      AcceptPrivacy: [false, [Validators.requiredTrue]],
      Regalo: [false],
      EmailDestinatarioRegalo: [null],
    });

    this.setSlides();
    this.loadAvailableDates();
    this.cdr.detectChanges();
    this.calcolaTotale();
  }

  get form() {
    return this.visitForm.controls;
  }

  reserveNow() {
    this.submitted = true;
    const Regalo = this.visitForm.get("Regalo")?.value;

    if (this.visitForm.get("AcceptPrivacy")?.invalid) {
      const el = this.privacyCheckbox?.nativeElement;
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      el?.focus();
    }

    if (
      this.visitForm.valid &&
      ((this.visitForm.get("VisitTime")?.value &&
        this.visitForm.get("VisitTime")?.value !== "") ||
        Regalo === true)
    ) {
      const FirstName = this.visitForm.get("FirstName")?.value;
      const LastName = this.visitForm.get("LastName")?.value;
      const Phone = this.visitForm.get("Phone")?.value;
      const Email = this.visitForm.get("Email")?.value;
      const Nperson = this.visitForm.get("Nperson")?.value;
      const Nchild = this.visitForm.get("Nchild")?.value;
      const NchildUnder6 = this.visitForm.get("NchildUnder6")?.value;
      const Language = this.visitForm.get("Language")?.value;
      const VisitDate = this.visitForm.get("VisitDate")?.value;
      const VisitTimeForm = this.visitForm.get("VisitTime")?.value;
      const Tasting = this.visitForm.get("Tasting")?.value;
      const Tasting2 = this.visitForm.get("Tasting2")?.value;
      const Tasting3 = this.visitForm.get("Tasting3")?.value;
      const Paese = this.visitForm.get("Paese")?.value;
      const EmailDestinatarioRegalo = this.visitForm.get(
        "EmailDestinatarioRegalo",
      )?.value;

      if (VisitTimeForm || (VisitTimeForm === "" && Regalo == true)) {
        const selectedVisitTime = this.timeOptions.find(
          (item) => item.id === VisitTimeForm,
        );

        const VisitTime =
          selectedVisitTime && selectedVisitTime.id
            ? selectedVisitTime.ora
            : null;

        if (Paese === "" || Paese === null || Paese === undefined) {
          var newVisitData = {
            FirstName: FirstName,
            LastName: LastName,
            Phone: Number(Phone),
            Email: Email,
            Nperson: Number(Nperson),
            Nchild: Number(Nchild),
            NchildUnder6: Number(NchildUnder6 || 0),
            Language: Language,
            VisitDate: VisitDate,
            VisitTime: VisitTime,
            Tasting: Tasting,
            Tasting2: Tasting2,
            Tasting3: Tasting3,
            Total: this.totaleVisita,
            Regalo: Regalo,
            EmailDestinatarioRegalo: EmailDestinatarioRegalo,
          };

          this.httpVisit.saveVisitData(newVisitData).subscribe((response) => {
            this.submitted = false;
            this.visitForm.reset();
            var idVisita = response;
            if (idVisita !== undefined) {
              this.makePayment(newVisitData, idVisita);
            }
          });
        }
      }
    }
  }

  makePayment(visitData: any, idVisita: any) {
    //configurazione stripe
    console.log("inizio configurazione stripe");
    console.log("pagamento di: ", visitData.Total, " euro");
    let payload = {
      amount: visitData.Total * 100,
      currency: "EUR",
      email: visitData.Email,
      visit_id: idVisita,
      name: visitData.FirstName,
      surname: visitData.LastName,
      phone: visitData.Phone,
      n_person: visitData.Nperson,
      n_child: visitData.Nchild,
      visit_date: visitData.VisitDate,
      visit_time: visitData.VisitTime,
      language: visitData.Language,
      tasting: visitData.Tasting,
      tasting2: visitData.Tasting2,
      tasting3: visitData.Tasting3,
    };
    this.httpVisit.payVisit(payload).subscribe(
      (link: string) => {
        if (link) {
          window.location.href = link;
          // window.open(link, "_blank");
        }
      },
      (err) => {
        window.location.href = err.error.tex;
        //window.open(err.error.text, "_blank");
      },
    );
  }

  calcolaTotale(): Promise<void> {
    return new Promise((resolve, reject) => {
      const visitTime = this.visitForm.get("VisitTime")?.value;
      const isRegalo = this.visitForm.get("Regalo")?.value === true;
      const Language = this.visitForm.get("Language")?.value;

      const selectedIndex = this.timeOptions.findIndex(
        (item) => item.id !== "" && item.id === visitTime,
      );

      // I prezzi dal BE si mostrano solo se è un regalo oppure se sono state scelte data e ora
      if (selectedIndex === -1 && !isRegalo) {
        this.prezzi = null;
        this.totaleVisita = 0;
        this.scheduleStickyTicketUpdate();
        resolve();
        return;
      }

      // Se è regalo ma non c'è VisitTime, usa comunque il primo slot valido
      const timeId =
        selectedIndex !== -1 ? this.timeOptions[selectedIndex].id : "0";

      this.httpVisit.getVisitPrices(timeId, Language).subscribe(
        (data) => {
          this.prezzi = data.price;
          this.isDegustazioneAvailable = data.degustazione_semplice;
          this.isDegustazioneAnoliniAvailable = data.degustazione_anolini;
          this.isDegustazionePaninoAvailable = data.degustazione_panino;

          const Nperson = Number(this.visitForm.get("Nperson")?.value || 0);
          const Nchild = Number(this.visitForm.get("Nchild")?.value || 0);

          let Tasting = this.visitForm.get("Tasting")?.value === true;
          let Tasting2 = this.visitForm.get("Tasting2")?.value === true;
          let Tasting3 = this.visitForm.get("Tasting3")?.value === true;

          // Disabilita degustazioni non disponibili
          if (!this.isDegustazioneAvailable) {
            Tasting = false;
            this.visitForm
              .get("Tasting")
              ?.patchValue(false, { emitEvent: false });
          }

          if (!this.isDegustazioneAnoliniAvailable) {
            Tasting2 = false;
            this.visitForm
              .get("Tasting2")
              ?.patchValue(false, { emitEvent: false });
          }

          if (!this.isDegustazionePaninoAvailable) {
            Tasting3 = false;
            this.visitForm
              .get("Tasting3")
              ?.patchValue(false, { emitEvent: false });
          }

          // Calcolo base
          this.totaleVisita =
            Nperson * this.prezzi.PrezzoAdulto +
            Nchild * this.prezzi.PrezzoBambino;

          if (Tasting) {
            this.totaleVisita =
              Nperson * this.prezzi.PrezzoAdultoDeg +
              Nchild * this.prezzi.PrezzoBambinoDeg;
          }

          if (Tasting2) {
            this.totaleVisita =
              Nperson * this.prezzi.PrezzoAdultoAnolini +
              Nchild * this.prezzi.PrezzoBambinoAnolini;
          }

          if (Tasting3) {
            this.totaleVisita =
              Nperson * this.prezzi.PrezzoAdultoPanino +
              Nchild * this.prezzi.PrezzoBambinoPanino;
          }

          this.scheduleStickyTicketUpdate();
          resolve();
        },
        (error) => reject(error),
      );
    });
  }

  openNewTab() {
    window.open("/privacy", "_blank");
  }

  private setSlides() {
    this.slidersService.getVisitSlides().subscribe((res) => {
      this.sliders = res;
    });
  }

  private ngbDateToString(date: NgbDateStruct): string {
    const mm = String(date.month).padStart(2, "0");
    const dd = String(date.day).padStart(2, "0");
    return `${date.year}-${mm}-${dd}`;
  }

  private formatDateForApi(date: Date): string {
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${date.getFullYear()}-${mm}-${dd}`;
  }

  loadAvailableDates() {
    const Language = this.visitForm.get("Language")?.value;
    const start = this.formatDateForApi(new Date(this.tomorrow));
    const endDate = new Date(this.tomorrow);
    endDate.setMonth(endDate.getMonth() + 6);
    const end = this.formatDateForApi(endDate);

    this.httpVisit
      .getVisitDatesAvailable(Language, start, end)
      .subscribe((dates) => {
        this.availableDates = new Set(dates);

        const currentDate = this.visitForm.get("VisitDate")?.value;
        if (currentDate && !this.availableDates.has(currentDate)) {
          this.visitForm.get("VisitDate")?.setValue("");
          this.dateModel = null;
          this.changeVisitDate();
        }
      });
  }

  onLanguageChange() {
    this.loadAvailableDates();
    this.changeVisitDate();
    this.calcolaTotale();
  }

  onDateSelect(date: NgbDateStruct) {
    this.dateModel = date;
    this.visitForm.get("VisitDate")?.setValue(this.ngbDateToString(date));
    this.changeVisitDate();
  }

  get hasTimeOptions(): boolean {
    return this.timeOptions.some((item) => item.id !== "");
  }

  get formulaLabel(): string {
    if (this.visitForm.get("Tasting3")?.value) {
      return "visite.form.DegustazionePanino";
    }
    if (this.visitForm.get("Tasting2")?.value) {
      return "visite.form.DegustazioneAnolini";
    }
    if (this.visitForm.get("Tasting")?.value) {
      return "visite.form.Degustazione";
    }
    return "solo visita guidata";
  }

  get formattedVisitDate(): string {
    const raw = this.visitForm.get("VisitDate")?.value;
    if (!raw) {
      return "";
    }
    const [y, m, d] = raw.split("-");
    return `${d}-${m}-${y}`;
  }

  get formattedVisitTime(): string {
    const id = this.visitForm.get("VisitTime")?.value;
    const found = this.timeOptions.find((item) => item.id === id);
    return found ? found.ora : "";
  }

  setLanguage(lang: string) {
    if (this.visitForm.get("Language")?.value === lang) {
      return;
    }
    this.visitForm.get("Language")?.setValue(lang);
    this.onLanguageChange();
  }

  setFormula(kind: "solo" | "degustazione" | "anolini" | "panino") {
    this.visitForm.get("Tasting")?.setValue(kind === "degustazione");
    this.visitForm.get("Tasting2")?.setValue(kind === "anolini");
    this.visitForm.get("Tasting3")?.setValue(kind === "panino");
    this.calcolaTotale();
  }

  adjustGuests(control: "Nperson" | "Nchild" | "NchildUnder6", delta: number) {
    const min = control === "Nperson" ? 1 : 0;
    const current = Number(this.visitForm.get(control)?.value) || 0;
    this.visitForm.get(control)?.setValue(Math.max(min, current + delta));
    this.calcolaTotale();
  }

  selectVisitTime(id: string) {
    this.visitForm.get("VisitTime")?.setValue(id);
    this.calcolaTotale();
  }

  async changeVisitDate() {
    const Nperson = this.visitForm.get("Nperson")?.value;
    const Nchild = this.visitForm.get("Nchild")?.value;
    const VisitDate = this.visitForm.get("VisitDate")?.value;
    const VisitLang = this.visitForm.get("Language")?.value;
    const tot = Number(Nperson) + Number(Nchild);
    if (VisitDate) {
      this.visitForm.get("VisitTime")?.setValidators(Validators.required);
      this.visitForm.get("VisitTime")?.updateValueAndValidity();
      if (tot && tot !== 0) {
        const response = await this.httpVisit
          .setVisitTimes(VisitDate, VisitLang, tot)
          .toPromise();

        if (response && response.length > 0) {
          this.timeOptions = response;
          this.visitForm.get("VisitTime").setValue(response[0].id); // Imposta il primo orario disponibile

          // Carica i prezzi per il primo orario e calcola il totale
          await this.calcolaTotale();
        } else {
          this.timeOptions = [
            {
              id: "",
              ora: "Nessun orario disponibile per questa data",
            },
          ];
          this.visitForm.get("VisitTime").setValue("");
          this.totaleVisita = 0;
        }
      } else {
        this.timeOptions = [
          {
            id: "",
            ora: "Seleziona data e lingua",
          },
        ];
        this.visitForm.get("VisitTime").setValue("");
        this.totaleVisita = 0;
      }
    } else if (
      (!VisitDate || VisitDate === "") &&
      this.visitForm.get("Regalo")?.value == true
    ) {
      this.visitForm.get("VisitTime")?.clearValidators();
      this.visitForm.get("VisitTime")?.setValidators(Validators.nullValidator);
      this.visitForm.get("VisitTime")?.updateValueAndValidity();
    }
  }

  setAsRegalo(event: any) {
    if (event && event.target.checked) {
      this.dateModel = null;
      this.visitForm.get("VisitDate")?.setValue("");
      this.visitForm.get("VisitDate")?.clearValidators();
      this.visitForm.get("VisitDate")?.setValidators(Validators.nullValidator);

      this.visitForm.get("VisitTime")?.setValue("");
      this.visitForm.get("VisitTime")?.clearValidators();
      this.visitForm.get("VisitTime")?.setValidators(Validators.nullValidator);

      this.visitForm
        .get("EmailDestinatarioRegalo")
        ?.setValidators(Validators.required);

      this.isDegustazioneAnoliniAvailable = true;

      // Il DOM va aggiornato in modo sincrono prima del focus: sui browser mobili
      // (es. Safari iOS) l'apertura della tastiera funziona solo se .focus() viene
      // chiamato entro lo stesso gesto utente, quindi niente setTimeout/promise qui.
      this.cdr.detectChanges();
      this.emailDestinatarioRegaloInput?.nativeElement.focus();
    } else {
      this.visitForm.get("EmailDestinatarioRegalo")?.setValue("");
      this.visitForm.get("EmailDestinatarioRegalo")?.clearValidators();
      this.visitForm
        .get("EmailDestinatarioRegalo")
        ?.setValidators(Validators.nullValidator);
      this.visitForm.get("VisitDate")?.setValidators(Validators.required);
      this.visitForm.get("VisitTime")?.setValidators(Validators.required);
    }

    this.visitForm.get("EmailDestinatarioRegalo")?.updateValueAndValidity();
    this.visitForm.get("VisitDate")?.updateValueAndValidity();
    this.visitForm.get("VisitTime")?.updateValueAndValidity();

    this.calcolaTotale();
  }
}
